import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, CheckCircle2, Shield, Clock, Zap } from 'lucide-react';

const CTASection = ({ onOpenContactForm, imagePosition = 'right', heading, paragraph, buttonText, imageSrc }) => {
  return (
    <section className="relative py-20 bg-gradient-to-br from-[#232F3E] via-[#1a1a2e] to-gray-900 overflow-hidden">
      {/* Curved Background Shape */}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-gray-900 to-transparent"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FF9900] rounded-full opacity-5 blur-3xl"></div>
      
      {/* Wavy Divider Top */}
      <div className="absolute top-0 left-0 w-full overflow-hidden leading-none rotate-180">
        <svg className="relative block w-full h-16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" className="fill-[#1a1a2e]"></path>
        </svg>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-stretch">
          {/* Content */}
          <div className={`space-y-6 flex flex-col justify-between ${imagePosition === 'right' ? 'order-1' : 'order-1 lg:order-2'}`}>
            <div>
              <div className="inline-block bg-gradient-to-r from-[#FF9900]/20 to-orange-600/20 border border-[#FF9900]/30 text-[#FF9900] px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Take Action Now
              </div>

              <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                {heading}
              </h2>

              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                {paragraph}
              </p>

              {/* Key Benefits List */}
              <div className="space-y-4 mb-6">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-[#FF9900] to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Federal Protection Guaranteed</h4>
                    <p className="text-gray-400 text-sm">Exclusive nationwide rights to your brand</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-[#FF9900] to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Complete Legal Coverage</h4>
                    <p className="text-gray-400 text-sm">Stop infringement and protect your identity</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-[#FF9900] to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Fast Turnaround Time</h4>
                    <p className="text-gray-400 text-sm">Quick processing with expert guidance</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-[#FF9900] to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Unlock Brand Registry</h4>
                    <p className="text-gray-400 text-sm">Access powerful Amazon seller tools</p>
                  </div>
                </div>
              </div>

              {/* Additional Info Box */}
              <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <h4 className="text-[#FF9900] font-bold mb-2 flex items-center">
                  <Shield className="w-5 h-5 mr-2" />
                  Your Brand, Your Future
                </h4>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Every successful business starts with protecting what makes it unique. Our trademark registration services ensure your brand identity remains yours alone, giving you the legal foundation to grow confidently in any market.
                </p>
              </div>
            </div>

            <div>
              <Button
                onClick={onOpenContactForm}
                size="lg"
                className="bg-gradient-to-r from-[#FF9900] to-orange-600 hover:from-[#FF9900]/90 hover:to-orange-600/90 text-black px-8 py-6 text-lg font-semibold shadow-lg"
              >
                {buttonText}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Image - Full Height with Glowing Effect */}
          <div className={`${imagePosition === 'right' ? 'order-2' : 'order-2 lg:order-1'}`}>
            <div className="relative h-full group">
              <div className="absolute inset-0 bg-gradient-to-r from-[#FF9900] to-orange-600 rounded-3xl blur-2xl opacity-30 group-hover:opacity-40 transition-opacity"></div>
              <img
                src={imageSrc}
                alt="Call to action"
                className="relative w-full h-full object-cover rounded-3xl shadow-2xl border-4 border-[#FF9900]/30"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Wavy Divider Bottom */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
        <svg className="relative block w-full h-16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" className="fill-gray-900"></path>
        </svg>
      </div>
    </section>
  );
};

export default CTASection;